import React, { useState } from "react";
import { Button, TextInput, Select, MultiSelect, CustomCheckbox } from "@/components";
import { Plus, X, ChevronDown, ChevronUp } from "lucide-react";
import type { 
  CreateAgentsParams, 
  SingleAgentConfig,
  AgentGender,
  AgentAgeGroup,
  AgentBodyType,
  AgentEthnicity,
  AgentHairType,
  AgentAccessory,
  AgentDressing,
  AgentBeard,
} from "../interfaces";
import {
  getBodyTypeOptions,
  getBeardOptions,
  getHairTypeOptions,
  autoCorrectAttributes,
} from "../utils/attributeRules";

// Options for dropdowns
const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'non-binary', label: 'Non-Binary' },
];

const AGE_GROUP_OPTIONS = [
  { value: 'early 20s', label: 'Early 20s' },
  { value: 'mid 20s', label: 'Mid 20s' },
  { value: 'late 20s', label: 'Late 20s' },
  { value: 'early 30s', label: 'Early 30s' },
  { value: 'mid 30s', label: 'Mid 30s' },
  { value: 'late 30s', label: 'Late 30s' },
  { value: 'early 40s', label: 'Early 40s' },
  { value: 'mid 40s', label: 'Mid 40s' },
];

const ETHNICITY_OPTIONS = [
  { value: 'American', label: 'American' },
  { value: 'African', label: 'African' },
  { value: 'East Asian', label: 'East Asian' },
  { value: 'South Asian', label: 'South Asian' },
  { value: 'Middle Eastern', label: 'Middle Eastern' },
  { value: 'European', label: 'European' },
  { value: 'Latino', label: 'Latino' },
  { value: 'Caribbean', label: 'Caribbean' },
  { value: 'Mixed', label: 'Mixed' },
];

const ACCESSORIES_OPTIONS = [
  { value: 'glasses', label: 'Glasses' },
  { value: 'sunglasses', label: 'Sunglasses' },
  { value: 'beanie', label: 'Beanie' },
  { value: 'baseball_cap', label: 'Baseball Cap' },
  { value: 'fedora', label: 'Fedora' },
  { value: 'jewelry', label: 'Jewelry' },
  { value: 'watch', label: 'Watch' },
  { value: 'earrings', label: 'Earrings' },
  { value: 'necklace', label: 'Necklace' },
  { value: 'scarf', label: 'Scarf' },
  { value: 'none', label: 'None' },
];

const DRESSING_OPTIONS = [
  { value: 'casual', label: 'Casual' },
  { value: 'business casual', label: 'Business Casual' },
  { value: 'formal', label: 'Formal' },
  { value: 'streetwear', label: 'Streetwear' },
  { value: 'sporty', label: 'Sporty' },
  { value: 'minimalist', label: 'Minimalist' },
  { value: 'creative', label: 'Creative' },
  { value: 'bohemian', label: 'Bohemian' },
  { value: 'vintage', label: 'Vintage' },
];

type AgentFormData = {
  name: string;
  autoGenerate: boolean;
  customization: {
    gender?: AgentGender;
    ageGroup?: AgentAgeGroup;
    bodyType?: AgentBodyType;
    ethnicity?: AgentEthnicity;
    hairType?: AgentHairType;
    accessories?: AgentAccessory[];
    dressing?: AgentDressing;
    beard?: AgentBeard;
  };
};

interface CreateAgentsFormProps {
  onSubmit: (data: CreateAgentsParams) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

export const CreateAgentsForm: React.FC<CreateAgentsFormProps> = ({
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [agents, setAgents] = useState<AgentFormData[]>([
    {
      name: "",
      autoGenerate: false,
      customization: {},
    },
  ]);

  const [expandedAgents, setExpandedAgents] = useState<Set<number>>(new Set([0]));
  const [errors, setErrors] = useState<Record<number, string>>({});

  const toggleExpanded = (index: number) => {
    const newExpanded = new Set(expandedAgents);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedAgents(newExpanded);
  };

  const handleAddAgent = () => {
    if (agents.length >= 10) {
      return;
    }
    const newAgents = [
      ...agents,
      {
        name: "",
        autoGenerate: false,
        customization: {},
      },
    ];
    setAgents(newAgents);
    setExpandedAgents(new Set([...expandedAgents, agents.length]));
  };

  const handleRemoveAgent = (index: number) => {
    if (agents.length <= 1) return;
    const newAgents = agents.filter((_, i) => i !== index);
    setAgents(newAgents);
    
    // Update expanded state
    const newExpanded = new Set<number>();
    expandedAgents.forEach((i) => {
      if (i < index) {
        newExpanded.add(i);
      } else if (i > index) {
        newExpanded.add(i - 1);
      }
    });
    setExpandedAgents(newExpanded);

    // Clear error for this agent
    const newErrors = { ...errors };
    delete newErrors[index];
    setErrors(newErrors);
  };

  const updateAgent = (index: number, updates: Partial<AgentFormData>) => {
    setAgents((prevAgents) => {
      const newAgents = [...prevAgents];
      
      // If toggling autoGenerate to true, clear customization
      if (updates.autoGenerate === true && newAgents[index].autoGenerate === false) {
        newAgents[index] = { 
          ...newAgents[index], 
          ...updates,
          customization: {} 
        };
      } else {
        newAgents[index] = { ...newAgents[index], ...updates };
      }
      
      return newAgents;
    });

    // Clear error when user makes changes
    if (errors[index]) {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors[index];
        return newErrors;
      });
    }
  };

  const updateCustomization = (
    index: number,
    field: keyof AgentFormData["customization"],
    value: any
  ) => {
    setAgents((prevAgents) => {
      const newAgents = [...prevAgents];
      const currentCustomization = newAgents[index].customization;
      
      // Update the field
      const updatedCustomization = {
        ...currentCustomization,
        [field]: value,
      };
      
      // Auto-correct dependent fields when gender or ethnicity changes
      if (field === 'gender' || field === 'ethnicity') {
        const corrections = autoCorrectAttributes({
          gender: field === 'gender' ? value : currentCustomization.gender,
          bodyType: currentCustomization.bodyType,
          ethnicity: field === 'ethnicity' ? value : currentCustomization.ethnicity,
          hairType: currentCustomization.hairType,
          beard: currentCustomization.beard,
        });
        
        // Apply corrections (clear incompatible values)
        Object.assign(updatedCustomization, corrections);
      }
      
      newAgents[index] = {
        ...newAgents[index],
        customization: updatedCustomization,
      };
      return newAgents;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate
    const newErrors: Record<number, string> = {};
    agents.forEach((agent, index) => {
      if (!agent.name.trim()) {
        newErrors[index] = "Agent name is required";
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Transform to API format
    const agentConfigs: SingleAgentConfig[] = agents.map((agent) => {
      const config: SingleAgentConfig = {
        name: agent.name.trim(),
      };

      // If autoGenerate is true and no customization, just return name
      if (agent.autoGenerate) {
        config.autoGenerate = true;
        // Don't include customization for full auto-generation
        return config;
      }

      // If autoGenerate is false, user is customizing
      config.autoGenerate = false;

      // Only include customization if there are any custom values
      const hasCustomization = Object.keys(agent.customization).length > 0;
      if (hasCustomization) {
        config.customization = {};
        
        if (agent.customization.gender) {
          config.customization.gender = agent.customization.gender;
        }
        if (agent.customization.ageGroup) {
          config.customization.ageGroup = agent.customization.ageGroup;
        }
        if (agent.customization.bodyType) {
          config.customization.bodyType = agent.customization.bodyType;
        }
        if (agent.customization.ethnicity) {
          config.customization.ethnicity = agent.customization.ethnicity;
        }
        if (agent.customization.hairType) {
          config.customization.hairType = agent.customization.hairType;
        }
        if (agent.customization.accessories && agent.customization.accessories.length > 0) {
          config.customization.accessories = agent.customization.accessories;
        }
        if (agent.customization.dressing) {
          config.customization.dressing = agent.customization.dressing;
        }
      }

      return config;
    });

    await onSubmit({ agents: agentConfigs });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        {agents.map((agent, index) => {
          const isExpanded = expandedAgents.has(index);
          return (
            <div
              key={index}
              className="border border-input rounded-lg p-4 bg-card"
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="flex-1">
                  <TextInput
                    label={`Agent ${index + 1} Name`}
                    value={agent.name}
                    onChange={(e) =>
                      updateAgent(index, { name: e.target.value })
                    }
                    placeholder="e.g., Sarah, Marcus, Kenji"
                    error={!!errors[index]}
                    helperText={errors[index]}
                  />
                </div>
                <div className="flex gap-2 mt-7">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => toggleExpanded(index)}
                    title={isExpanded ? "Collapse" : "Expand"}
                  >
                    {isExpanded ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </Button>
                  {agents.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveAgent(index)}
                      title="Remove agent"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>

              {isExpanded && (
                <div className="space-y-4 pl-0 sm:pl-4 border-l-2 border-muted">
                  {/* Auto Generate Toggle */}
                  <div className="flex items-center gap-3">
                    <CustomCheckbox
                      id={`auto-generate-${index}`}
                      checked={agent.autoGenerate}
                      onChange={(checked) => {
                        updateAgent(index, { autoGenerate: checked });
                      }}
                    />
                    <label
                      htmlFor={`auto-generate-${index}`}
                      className="text-sm font-medium cursor-pointer select-none"
                    >
                      Auto-generate all attributes
                    </label>
                  </div>

                  <div className="text-xs text-muted-foreground mb-4">
                    {agent.autoGenerate
                      ? "All attributes will be randomly generated"
                      : "Customize the agent's attributes below"}
                  </div>

                  {/* Customization Fields - Only show when auto-generate is OFF */}
                  {!agent.autoGenerate && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Select
                      label="Gender"
                      value={agent.customization.gender || ""}
                      onChange={(val) =>
                        updateCustomization(index, "gender", val as AgentGender)
                      }
                      options={GENDER_OPTIONS}
                      placeholder="Select gender..."
                    />

                    <Select
                      label="Age Group"
                      value={agent.customization.ageGroup || ""}
                      onChange={(val) =>
                        updateCustomization(
                          index,
                          "ageGroup",
                          val as AgentAgeGroup
                        )
                      }
                      options={AGE_GROUP_OPTIONS}
                      placeholder="Select age group..."
                    />

                    <Select
                      label="Body Type"
                      value={agent.customization.bodyType || ""}
                      onChange={(val) =>
                        updateCustomization(
                          index,
                          "bodyType",
                          val as AgentBodyType
                        )
                      }
                      options={getBodyTypeOptions(agent.customization.gender)}
                      placeholder="Select body type..."
                      helperText={agent.customization.gender ? `Options filtered for ${agent.customization.gender}` : undefined}
                    />

                    <Select
                      label="Ethnicity"
                      value={agent.customization.ethnicity || ""}
                      onChange={(val) =>
                        updateCustomization(
                          index,
                          "ethnicity",
                          val as AgentEthnicity
                        )
                      }
                      options={ETHNICITY_OPTIONS}
                      placeholder="Select ethnicity..."
                    />

                    <Select
                      label="Hair Type"
                      value={agent.customization.hairType || ""}
                      onChange={(val) =>
                        updateCustomization(
                          index,
                          "hairType",
                          val as AgentHairType
                        )
                      }
                      options={getHairTypeOptions(agent.customization.ethnicity)}
                      placeholder="Select hair type..."
                      helperText={agent.customization.ethnicity ? `Options filtered for ${agent.customization.ethnicity}` : undefined}
                    />

                    <MultiSelect
                      label="Accessories"
                      value={(agent.customization.accessories || []) as string[]}
                      onChange={(val) =>
                        updateCustomization(
                          index,
                          "accessories",
                          val as AgentAccessory[]
                        )
                      }
                      options={ACCESSORIES_OPTIONS}
                      placeholder="Select accessories..."
                    />

                    <Select
                      label="Dressing"
                      value={agent.customization.dressing || ""}
                      onChange={(val) =>
                        updateCustomization(
                          index,
                          "dressing",
                          val as AgentDressing
                        )
                      }
                      options={DRESSING_OPTIONS}
                      placeholder="Select dressing style..."
                    />

                    <Select
                      label="Beard"
                      value={agent.customization.beard || ""}
                      onChange={(val) =>
                        updateCustomization(
                          index,
                          "beard",
                          val as AgentBeard
                        )
                      }
                      options={getBeardOptions(agent.customization.gender)}
                      placeholder="Select beard style..."
                      helperText={agent.customization.gender === 'female' ? 'Typically none for female agents' : agent.customization.gender ? `Options available for ${agent.customization.gender}` : undefined}
                    />
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add Agent Button */}
      {agents.length < 10 && (
        <Button
          type="button"
          variant="outline"
          onClick={handleAddAgent}
          className="w-full"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Another Agent ({agents.length}/10)
        </Button>
      )}

      {/* Form Actions */}
      <div className="flex gap-4 justify-end pt-4 border-t border-input">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? "Creating..." : `Create ${agents.length} Agent${agents.length > 1 ? "s" : ""}`}
        </Button>
      </div>
    </form>
  );
};
